import {
  FC,
  Fragment,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useData } from "data/data-provider";
import FleetCard from "designs/DesignB/components/FleetCard/FleetCard";
import CarDetailPanel from "designs/DesignB/components/CarDetails/CarDetailPanel";
import CarSheet from "designs/DesignB/components/CarDetails/CarSheet";

/**
 * Design B's fleet grid (1NOW-11219): a card opens the car's details in place, the way Design C's
 * results do, and Book now lives in those details instead of on the card.
 *
 * From 768px the details open as a panel after the last card of the chosen card's row, one at a
 * time, growing over 340ms (B's FAQ grid-rows pattern) with a notch pointing at the card. A card
 * in the same row swaps the panel's content; one in another row folds the old panel as the new
 * one opens. Below 768px a tap opens a full-screen sheet instead (CarSheet).
 *
 * As in Design C: the open car is `?selectedCarId=` in the URL (the data provider writes it from
 * the selected car) and a reload or a shared link opens it again, asking the page for more cards
 * when it is past "Load More"; a filter or a sort closes it; closing hands focus back to the card.
 */

const PHONE = "(max-width: 767px)";

const reducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const useMedia = (query: string) => {
  const read = () => typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [on, setOn] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const sync = () => setOn(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, [query]);
  return on;
};

/**
 * Columns the grid draws right now, read off the grid itself rather than guessed from breakpoints:
 * B's grids break differently (the home page has four columns from 1280px, the Vehicles page three).
 */
const useLiveColumns = (grid: RefObject<HTMLElement>) => {
  const [cols, setCols] = useState(1);
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const read = () => {
      const tracks = getComputedStyle(el).gridTemplateColumns;
      setCols(tracks && tracks !== "none" ? tracks.trim().split(/\s+/).length : 1);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [grid]);
  return cols;
};

/** Index of the last card in the row holding `index`. */
export const rowEndFor = (index: number, columns: number, count: number) =>
  Math.min(Math.floor(index / columns) * columns + columns - 1, count - 1);

/** Where the sticky header ends, so a scroll never leaves the card or panel under it. */
const headerBottom = () =>
  Array.from(document.querySelectorAll<HTMLElement>(".nc-Header, .nc-Header-pinned"))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.height > 0 && r.top <= 1)
    .reduce((low, r) => Math.max(low, r.bottom), 0);

type Id = string | number;
type Panel = { key: number; carId: Id; closing: boolean; instant: boolean; history: boolean };

/** The open/close of one panel: 0fr to 1fr and back, removed once folded. */
const PanelWrap: FC<{
  closing: boolean;
  instant: boolean;
  swapped: boolean;
  onGone: () => void;
  children: ReactNode;
  dataKey: number;
}> = ({ closing, instant, swapped, onGone, children, dataKey }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<"enter" | "open" | "settled">(() => (instant || reducedMotion() ? "settled" : "enter"));

  useLayoutEffect(() => {
    if (phase !== "enter") return;
    // The folded state is computed first, so the change to open is a transition.
    void ref.current?.offsetHeight;
    setPhase("open");
  }, [phase]);

  useEffect(() => {
    if (closing) {
      ref.current?.setAttribute("inert", "");
      if (reducedMotion()) {
        onGone();
        return;
      }
      const t = setTimeout(onGone, 260);
      return () => clearTimeout(t);
    }
    if (phase === "open") {
      const t = setTimeout(() => setPhase("settled"), 450);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing, phase]);

  const state = closing || phase === "enter" ? "" : phase === "open" ? "is-open" : "is-open is-settled";
  return (
    <div
      ref={ref}
      className={`db-xp ${state} ${closing ? "is-closing" : ""} ${swapped ? "is-swapped" : ""}`}
      data-db-xp={dataKey}
      onTransitionEnd={(e) => {
        // The panel's own fade (close) and rise (open) end the move; its children's transitions don't.
        if (!(e.target as HTMLElement).classList?.contains("db-xp-panel")) return;
        if (closing) {
          if (e.propertyName === "opacity") onGone();
        } else if (phase === "open" && e.propertyName === "transform") setPhase("settled");
      }}
    >
      <div className="db-xp__clip">
        <div className="db-xp__pad">{children}</div>
      </div>
      <span className="db-xp__notch" aria-hidden="true" />
    </div>
  );
};

export interface ExpandingFleetGridProps {
  /** The cards to show, in order: the page of results the caller renders. */
  cars: any[];
  /** The grid's column classes, e.g. "sm:grid-cols-2 xl:grid-cols-3". */
  gridClassName: string;
  /** Book now in the details: the page's own next step for this car. */
  onBook: (car: any) => void;
  /** The details close whenever this changes (a filter, a sort). */
  resetKey?: string;
  /** The whole ordered list `cars` is the first part of, when the caller pages it. */
  pool?: any[];
  /** Show at least this many cards: so a car the URL names past "Load More" can open. */
  onReveal?: (count: number) => void;
  /** Nothing more is coming into `pool`. */
  settled?: boolean;
}

let gridSeq = 0;

const ExpandingFleetGrid: FC<ExpandingFleetGridProps> = ({
  cars,
  gridClassName,
  onBook,
  resetKey,
  pool,
  onReveal,
  settled = true,
}) => {
  const { setSelectedCar } = useData();
  const grid = useRef<HTMLDivElement>(null);
  const columns = useLiveColumns(grid);
  const phone = useMedia(PHONE);
  const [uid] = useState(() => `db-xp-${++gridSeq}`);

  const [panels, setPanels] = useState<Panel[]>([]);
  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  const seq = useRef(0);
  const live = panels.find((p) => !p.closing) ?? null;
  const openId = live?.carId ?? null;
  // The first car a panel opened on, so a car swapped in can cross-fade.
  const firstCar = useRef(new Map<number, Id>());

  // A click (not a filter, not the first paint) asks for the scroll that brings the details in.
  const pendingReveal = useRef(false);
  const cellOf = (id: Id) => grid.current?.querySelector<HTMLElement>(`[data-db-card-id="${String(id).replace(/["\\]/g, "")}"]`) ?? null;
  const buttonOf = (id: Id) => cellOf(id)?.querySelector<HTMLElement>(".db-fc__cta") ?? null;
  const indexOf = (id: Id) => cars.findIndex((c: any) => c.id === id);

  const openCar = useCallback(
    (car: any, how: "click" | "url") => {
      const cur = panelsRef.current.find((p) => !p.closing);
      if (cur && cur.carId === car.id) return;
      setSelectedCar(car);
      pendingReveal.current = true;
      const sameRow =
        !phone &&
        cur &&
        indexOf(cur.carId) >= 0 &&
        Math.floor(indexOf(cur.carId) / columns) === Math.floor(indexOf(car.id) / columns);
      if (sameRow && cur) {
        setPanels(panelsRef.current.map((p) => (p === cur ? { ...p, carId: car.id } : p)));
        return;
      }
      const key = ++seq.current;
      firstCar.current.set(key, car.id);
      setPanels([
        ...panelsRef.current.map((p) => (p === cur ? { ...p, closing: true } : p)),
        { key, carId: car.id, closing: false, instant: how === "url", history: how === "click" },
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phone, columns, cars, setSelectedCar],
  );

  /** Close the open details; focus goes back to the card's button, and the card into view. */
  const close = useCallback(() => {
    const cur = panelsRef.current.find((p) => !p.closing);
    if (!cur) return;
    setPanels(panelsRef.current.map((p) => (p === cur ? { ...p, closing: true } : p)));
    const button = buttonOf(cur.carId);
    if (!button) return;
    button.focus({ preventScroll: true });
    const top = (button.closest("article") ?? button).getBoundingClientRect().top;
    const head = headerBottom() + 12;
    if (top < head) window.scrollBy({ top: top - head, behavior: reducedMotion() ? "auto" : "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gone = useCallback((key: number) => {
    firstCar.current.delete(key);
    setPanels((prev) => prev.filter((p) => p.key !== key));
  }, []);

  // A different set of cards (a filter, a sort) closes the details at once.
  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) {
      firstReset.current = false;
      return;
    }
    setPanels([]);
  }, [resetKey]);

  // A car that left the list ("Load Less", a filter) takes its details with it.
  useEffect(() => {
    if (openId != null && indexOf(openId) < 0) setPanels([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, openId]);

  // Details closed: no car is selected, so the URL stops naming one.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (openId != null) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    setSelectedCar(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  // Open the car the URL names, once: a reload or a shared link shows what was open.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || cars.length === 0) return;
    const wanted = new URLSearchParams(window.location.search).get("selectedCarId");
    if (!wanted) {
      restored.current = true;
      return;
    }
    const car = cars.find((c: any) => String(c.id) === wanted);
    if (car) {
      restored.current = true;
      openCar(car, "url");
      return;
    }
    const at = (pool ?? []).findIndex((c: any) => String(c.id) === wanted);
    if (at >= cars.length && onReveal) {
      onReveal(at + 1);
      return;
    }
    if (!settled) return;
    // Not here (booked out for these dates, filtered away): stop naming it.
    restored.current = true;
    setSelectedCar(null);
    const params = new URLSearchParams(window.location.search);
    if (params.get("selectedCarId") === wanted) {
      params.delete("selectedCarId");
      const search = params.toString();
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + (search ? `?${search}` : "") + window.location.hash,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, pool, settled]);

  // The notch points at the open card, and slides when the open card changes within the row.
  useLayoutEffect(() => {
    if (!live || phone || !grid.current) return;
    const wrap = grid.current.querySelector<HTMLElement>(`[data-db-xp="${live.key}"]`);
    const card = cellOf(live.carId);
    if (!wrap || !card) return;
    const g = grid.current.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    wrap.style.setProperty("--nx", `${c.left + c.width / 2 - g.left}px`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.key, live?.carId, columns, phone]);

  // After a click: focus the details, then scroll as little as it takes to show the card and its
  // panel, or pin a panel taller than the screen under the header. A panel folding above the card
  // is about to take its height away, so that height comes off the target now.
  useEffect(() => {
    if (!live || !pendingReveal.current) return;
    pendingReveal.current = false;
    if (phone || !grid.current) return;
    const wrap = grid.current.querySelector<HTMLElement>(`[data-db-xp="${live.key}"]`);
    const card = cellOf(live.carId);
    if (!wrap || !card) return;
    wrap.querySelector<HTMLElement>(".db-xp-panel")?.focus({ preventScroll: true });
    // The panel has its full height from the first frame (only paint moves), so the scroll is
    // measured on the next frame and runs alongside the reveal instead of fighting a growing row.
    const t = requestAnimationFrame(() => {
      const cardTop0 = card.getBoundingClientRect().top;
      const folding = Array.from(grid.current?.querySelectorAll<HTMLElement>(".db-xp.is-closing") ?? []).filter(
        (el) => el.getBoundingClientRect().top < cardTop0,
      );
      const shift = folding.reduce((sum, el) => sum + el.offsetHeight, 0);
      const head = headerBottom() + 12;
      const room = window.innerHeight - 12;
      const cardTop = cardTop0 - shift;
      const top = wrap.getBoundingClientRect().top - shift;
      const height = wrap.querySelector<HTMLElement>(".db-xp__pad")?.offsetHeight ?? 0;
      const bottom = top + height;
      let d = 0;
      if (bottom - cardTop <= room - head) d = cardTop < head ? cardTop - head : bottom > room ? bottom - room : 0;
      else if (height <= room - head) d = top < head ? top - head - 16 : bottom > room ? bottom - room : 0;
      else d = top - head - 8;
      if (Math.abs(d) > 1) window.scrollBy({ top: d, behavior: reducedMotion() ? "auto" : "smooth" });
    });
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.key, live?.carId]);

  const toggle = (car: any) => (openId === car.id ? close() : openCar(car, "click"));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && live && !phone) {
      e.preventDefault();
      close();
    }
  };

  const book = (car: any) => {
    setSelectedCar(car);
    onBook(car);
  };

  const panelFor = (p: Panel) => {
    const car = cars[indexOf(p.carId)];
    if (!car) return null;
    const isLive = p === live;
    return (
      <PanelWrap
        key={p.key}
        dataKey={p.key}
        closing={p.closing}
        instant={p.instant}
        swapped={firstCar.current.get(p.key) !== p.carId}
        onGone={() => gone(p.key)}
      >
        <CarDetailPanel
          key={String(p.carId)}
          car={car}
          id={isLive ? `${uid}-panel` : undefined}
          titleId={isLive ? `${uid}-title` : undefined}
          onClose={close}
          onBook={() => book(car)}
        />
      </PanelWrap>
    );
  };

  // Each panel sits after the last card of its car's row.
  const after = new Map<number, Panel[]>();
  if (!phone) {
    panels.forEach((p) => {
      const i = indexOf(p.carId);
      if (i < 0) return;
      const end = rowEndFor(i, columns, cars.length);
      after.set(end, [...(after.get(end) ?? []), p]);
    });
  }

  return (
    <>
      <div
        ref={grid}
        className={`db-xp-grid grid gap-x-4 ${gridClassName}`}
        data-cols={columns}
        onKeyDown={onKeyDown}
      >
        {cars.map((car: any, i: number) => (
          <Fragment key={car.id}>
            <div className="db-xp-cell" data-db-card-id={String(car.id)}>
              <FleetCard
                data={car}
                simpleImg={true}
                selected={openId === car.id}
                panelId={openId === car.id && !phone ? `${uid}-panel` : undefined}
                onClick={() => toggle(car)}
              />
            </div>
            {(after.get(i) ?? []).map(panelFor)}
          </Fragment>
        ))}
      </div>
      {phone &&
        panels.map((p) => {
          const car = cars[indexOf(p.carId)];
          if (!car) return null;
          return (
            <CarSheet
              key={p.key}
              car={car}
              titleId={`${uid}-sheet-${p.key}`}
              closing={p.closing}
              ownHistory={p.history}
              onClose={() => {
                if (panelsRef.current.some((x) => x.key === p.key && !x.closing)) close();
              }}
              onGone={() => gone(p.key)}
              onBook={() => book(car)}
            />
          );
        })}
    </>
  );
};

export default ExpandingFleetGrid;
